import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { AdminUsers } from "@/components/admin/AdminUsers";
import { AdminStats } from "@/components/admin/AdminStats";
import { AdminSimulations } from "@/components/admin/AdminSimulations";
import { AdminMaintenance } from "@/components/admin/AdminMaintenance";

const inputCls =
  "w-full rounded-xl border border-[color:var(--hairline)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[color:var(--ink)] transition-colors duration-200";
const labelCls = "block text-xs font-bold mb-1 mt-3";

const emptyQuestion = { blocco: 1, moduleId: "", lessonId: "", testo: "", opzioni: ["", "", "", ""], correttaIndex: 0, spiegazione: "", difficolta: "media", isExample: false };
const emptyModule = { titolo: "", ore: 1, blocco: 1, ordine: 0, descrizione: "" };
const emptyLesson = { moduleId: "", titolo: "", ordine: 0, type: "scheda", urlVideo: "", testoSintesi: "", riepilogoText: "", mappaJson: '{\n  "centro": "",\n  "rami": []\n}', hours: 0, intro: "", closing: "", sectionsEdit: [{ heading: "", pointsText: "", key: "" }], isExample: false };

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [qFilter, setQFilter] = useState("all");

  // dialogs
  const [qDialog, setQDialog] = useState(null); // {mode:'create'|'edit', data}
  const [mDialog, setMDialog] = useState(null);
  const [lDialog, setLDialog] = useState(null);
  const [deleting, setDeleting] = useState(null); // {type, id, label}
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/questions").then((r) => setQuestions(r.data)).catch(() => {});
    api.get("/modules").then((r) => setModules(r.data)).catch(() => {});
    api.get("/admin/lessons").then((r) => setLessons(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const moduleName = (id) => modules.find((m) => m.id === id)?.titolo || id;

  // ---------- QUESTIONS ----------
  const saveQuestion = async () => {
    const d = qDialog.data;
    if (!d.testo.trim() || d.opzioni.some((o) => !o.trim()) || !d.moduleId) {
      toast.error("Compila testo, modulo e tutte e 4 le opzioni.");
      return;
    }
    setBusy(true);
    const payload = { ...d, blocco: Number(d.blocco), correttaIndex: Number(d.correttaIndex), lessonId: d.lessonId || null };
    delete payload.id;
    delete payload.updatedAt;
    try {
      if (qDialog.mode === "edit") {
        await api.put(`/admin/questions/${qDialog.data.id}`, payload);
      } else {
        await api.post("/admin/questions", payload);
      }
      toast.success("Domanda salvata.");
      setQDialog(null);
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // ---------- MODULES ----------
  const saveModule = async () => {
    const d = mDialog.data;
    if (!d.titolo.trim()) {
      toast.error("Il titolo è obbligatorio.");
      return;
    }
    setBusy(true);
    const payload = { titolo: d.titolo, ore: Number(d.ore), blocco: Number(d.blocco), ordine: Number(d.ordine), descrizione: d.descrizione || "" };
    try {
      if (mDialog.mode === "edit") {
        await api.put(`/admin/modules/${d.id}`, payload);
      } else {
        await api.post("/admin/modules", payload);
      }
      toast.success("Modulo salvato.");
      setMDialog(null);
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // ---------- LESSONS ----------
  const saveLesson = async () => {
    const d = lDialog.data;
    if (!d.titolo.trim() || !d.moduleId) {
      toast.error("Titolo e modulo sono obbligatori.");
      return;
    }
    const isScheda = d.type === "scheda";
    let mappa = { centro: "", rami: [] };
    if (!isScheda) {
      try {
        mappa = JSON.parse(d.mappaJson);
      } catch {
        toast.error("La mappa concettuale non è un JSON valido.");
        return;
      }
    }
    setBusy(true);
    const payload = {
      moduleId: d.moduleId,
      titolo: d.titolo,
      ordine: Number(d.ordine),
      type: d.type || "classic",
      urlVideo: isScheda ? "" : d.urlVideo || "",
      testoSintesi: isScheda ? "" : d.testoSintesi || "",
      mappaConcettuale: mappa,
      riepilogo: isScheda ? [] : d.riepilogoText.split("\n").map((s) => s.trim()).filter(Boolean),
      hours: Number(d.hours) || 0,
      intro: isScheda ? d.intro || "" : "",
      closing: isScheda ? d.closing || "" : "",
      sections: isScheda
        ? (d.sectionsEdit || [])
            .map((s) => ({
              heading: (s.heading || "").trim(),
              points: (s.pointsText || "").split("\n").map((p) => p.trim()).filter(Boolean),
              ...((s.key || "").trim() ? { key: s.key.trim() } : {}),
            }))
            .filter((s) => s.heading || s.points.length)
        : [],
      isExample: !!d.isExample,
    };
    try {
      if (lDialog.mode === "edit") {
        await api.put(`/admin/lessons/${d.id}`, payload);
      } else {
        await api.post("/admin/lessons", payload);
      }
      toast.success("Lezione salvata.");
      setLDialog(null);
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const updateSection = (idx, patch) => {
    const sections = lDialog.data.sectionsEdit.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setLDialog({ ...lDialog, data: { ...lDialog.data, sectionsEdit: sections } });
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/${deleting.type}/${deleting.id}`);
      toast.success("Eliminato.");
      setDeleting(null);
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const filteredQuestions = qFilter === "all" ? questions : questions.filter((q) => String(q.blocco) === qFilter);

  // Distribuzione risposta corretta A/B/C/D per blocco (per individuare subito squilibri)
  const answerDistribution = [1, 2, 3, 4].map((b) => {
    const inBlock = questions.filter((q) => q.blocco === b);
    const counts = [0, 0, 0, 0];
    inBlock.forEach((q) => {
      if (q.correttaIndex >= 0 && q.correttaIndex <= 3) counts[q.correttaIndex] += 1;
    });
    const max = Math.max(...counts, 0);
    const total = inBlock.length;
    // squilibrio: una posizione supera il 35% con almeno 8 domande
    const skewed = total >= 8 && max / total > 0.35;
    return { blocco: b, counts, total, skewed };
  });

  return (
    <div className="pt-6">
      <h1 className="text-3xl font-extrabold tracking-tight fade-up">Gestione contenuti.</h1>
      <p className="mt-2 text-sm text-[color:var(--text-2)]">Crea e modifica moduli, lezioni e domande. Le modifiche sono subito attive.</p>

      {stats && (
        <div className="mt-5 grid grid-cols-4 gap-2" data-testid="admin-stats">
          {[
            ["Moduli", stats.modules],
            ["Lezioni", stats.lessons],
            ["Domande", stats.questions],
            ["Utenti", stats.users],
          ].map(([label, val]) => (
            <div key={label} className="rounded-2xl border border-[color:var(--hairline)] bg-white p-3 text-center">
              <p className="font-mono text-xl font-semibold">{val}</p>
              <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-2)] font-bold">{label}</p>
            </div>
          ))}
        </div>
      )}
      {stats && (
        <p className="mt-2 font-mono text-[11px] text-[color:var(--text-2)]">
          Domande per blocco — B1: {stats.questionsPerBlock["1"]} · B2: {stats.questionsPerBlock["2"]} · B3: {stats.questionsPerBlock["3"]} · B4: {stats.questionsPerBlock["4"]} (minimo 15 per le simulazioni)
        </p>
      )}

      <Tabs defaultValue="iscritti" className="mt-6">
        <TabsList className="grid grid-cols-3 sm:grid-cols-7 w-full h-auto rounded-2xl bg-[color:var(--bg-subtle)] p-1 gap-1">
          <TabsTrigger value="iscritti" className="rounded-full text-xs font-bold" data-testid="admin-tab-iscritti">Iscritti</TabsTrigger>
          <TabsTrigger value="statistiche" className="rounded-full text-xs font-bold" data-testid="admin-tab-statistiche">Statistiche</TabsTrigger>
          <TabsTrigger value="simulazioni" className="rounded-full text-xs font-bold" data-testid="admin-tab-simulazioni">Simulazioni</TabsTrigger>
          <TabsTrigger value="questions" className="rounded-full text-xs font-bold" data-testid="admin-tab-questions">Domande</TabsTrigger>
          <TabsTrigger value="modules" className="rounded-full text-xs font-bold" data-testid="admin-tab-modules">Moduli</TabsTrigger>
          <TabsTrigger value="lessons" className="rounded-full text-xs font-bold" data-testid="admin-tab-lessons">Lezioni</TabsTrigger>
          <TabsTrigger value="manutenzione" className="rounded-full text-xs font-bold" data-testid="admin-tab-manutenzione">Manutenzione</TabsTrigger>
        </TabsList>

        {/* ISCRITTI */}
        <TabsContent value="iscritti" className="mt-5">
          <AdminUsers />
        </TabsContent>

        {/* STATISTICHE */}
        <TabsContent value="statistiche" className="mt-5">
          <AdminStats />
        </TabsContent>

        {/* SIMULAZIONI */}
        <TabsContent value="simulazioni" className="mt-5">
          <AdminSimulations />
        </TabsContent>

        {/* QUESTIONS */}
        <TabsContent value="questions" className="mt-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <select value={qFilter} onChange={(e) => setQFilter(e.target.value)} className={`${inputCls} max-w-[160px]`} data-testid="admin-questions-filter">
              <option value="all">Tutti i blocchi</option>
              {[1, 2, 3, 4].map((b) => (
                <option key={b} value={String(b)}>{`Blocco ${b}`}</option>
              ))}
            </select>
            <button
              onClick={() => setQDialog({ mode: "create", data: { ...emptyQuestion, opzioni: ["", "", "", ""] } })}
              data-testid="admin-create-question-button"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
            >
              <Plus className="w-3.5 h-3.5" /> Nuova domanda
            </button>
          </div>
          {/* Distribuzione risposta corretta A/B/C/D per blocco */}
          <div className="mb-4 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-4" data-testid="admin-answer-distribution">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[color:var(--text-2)]">
              Distribuzione risposta corretta (A / B / C / D)
            </p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {answerDistribution.map(({ blocco, counts, total, skewed }) => (
                <div
                  key={blocco}
                  data-testid={`admin-answer-dist-block-${blocco}`}
                  className={`rounded-xl border bg-white p-3 ${skewed ? "border-[color:var(--danger)]" : "border-[color:var(--hairline)]"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-wide">Blocco {blocco}</span>
                    {skewed && (
                      <span className="text-[9px] font-extrabold uppercase tracking-wide rounded-full px-1.5 py-0.5 text-white" style={{ background: "var(--danger)" }}>
                        Squilibrio
                      </span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-4 gap-1 text-center">
                    {["A", "B", "C", "D"].map((letter, i) => {
                      const pct = total ? Math.round((counts[i] / total) * 100) : 0;
                      return (
                        <div key={letter}>
                          <p className="font-mono text-sm font-semibold leading-4">{counts[i]}</p>
                          <p className="text-[9px] font-bold text-[color:var(--text-2)]">{letter} · {pct}%</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2" data-testid="admin-questions-list">
            {filteredQuestions.map((q) => (
              <div key={q.id} className="rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3 flex items-start gap-3">
                <span className="font-mono text-[10px] uppercase tracking-wide border border-[color:var(--hairline)] rounded-full px-2 py-0.5 mt-0.5 shrink-0">B{q.blocco}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-5 line-clamp-2">{q.testo}</p>
                  <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-1 truncate">{moduleName(q.moduleId)} · {q.difficolta}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => setQDialog({ mode: "edit", data: { ...q, lessonId: q.lessonId || "" } })}
                    aria-label="Modifica domanda"
                    data-testid="admin-edit-question-button"
                    className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleting({ type: "questions", id: q.id, label: "questa domanda" })}
                    aria-label="Elimina domanda"
                    data-testid="admin-delete-question-button"
                    className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-red-50 text-[color:var(--danger)] transition-colors duration-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* MODULES */}
        <TabsContent value="modules" className="mt-5">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setMDialog({ mode: "create", data: { ...emptyModule } })}
              data-testid="admin-create-module-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
            >
              <Plus className="w-3.5 h-3.5" /> Nuovo modulo
            </button>
          </div>
          <div className="space-y-2" data-testid="admin-modules-table">
            {modules.map((m) => (
              <div key={m.id} className="rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3 flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase border border-[color:var(--hairline)] rounded-full px-2 py-0.5 shrink-0">B{m.blocco}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate">{m.titolo}</p>
                  <p className="font-mono text-[11px] text-[color:var(--text-2)]">{m.ore}h · ordine {m.ordine}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setMDialog({ mode: "edit", data: { ...m } })} aria-label="Modifica modulo" data-testid="admin-edit-module-button" className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDeleting({ type: "modules", id: m.id, label: `il modulo “${m.titolo}” (incluse lezioni e domande collegate)` })} aria-label="Elimina modulo" data-testid="admin-delete-module-button" className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-red-50 text-[color:var(--danger)] transition-colors duration-200">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* LESSONS */}
        <TabsContent value="lessons" className="mt-5">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setLDialog({ mode: "create", data: { ...emptyLesson } })}
              data-testid="admin-create-lesson-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
            >
              <Plus className="w-3.5 h-3.5" /> Nuova lezione
            </button>
          </div>
          <div className="space-y-2" data-testid="admin-lessons-list">
            {lessons.map((l) => (
              <div key={l.id} className="rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate">{l.titolo}</p>
                  <p className="font-mono text-[11px] text-[color:var(--text-2)] truncate">
                    {moduleName(l.moduleId)} · {l.type === "scheda" ? `Scheda di studio${l.hours ? ` · ${l.hours}h` : ""}` : `ordine ${l.ordine}`}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() =>
                      setLDialog({
                        mode: "edit",
                        data: {
                          ...l,
                          type: l.type || "classic",
                          hours: l.hours || 0,
                          intro: l.intro || "",
                          closing: l.closing || "",
                          sectionsEdit: (l.sections || []).length
                            ? l.sections.map((s) => ({ heading: s.heading || "", pointsText: (s.points || []).join("\n"), key: s.key || "" }))
                            : [{ heading: "", pointsText: "", key: "" }],
                          riepilogoText: (l.riepilogo || []).join("\n"),
                          mappaJson: JSON.stringify(l.mappaConcettuale || { centro: "", rami: [] }, null, 2),
                        },
                      })
                    }
                    aria-label="Modifica lezione"
                    data-testid="admin-edit-lesson-button"
                    className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDeleting({ type: "lessons", id: l.id, label: `la lezione “${l.titolo}”` })} aria-label="Elimina lezione" data-testid="admin-delete-lesson-button" className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-red-50 text-[color:var(--danger)] transition-colors duration-200">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* MANUTENZIONE */}
        <TabsContent value="manutenzione" className="mt-5">
          <AdminMaintenance />
        </TabsContent>
      </Tabs>

      {/* QUESTION DIALOG */}
      <Dialog open={!!qDialog} onOpenChange={(o) => !o && setQDialog(null)}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-extrabold tracking-tight">{qDialog?.mode === "edit" ? "Modifica domanda" : "Nuova domanda"}</DialogTitle>
            <DialogDescription>4 opzioni, una sola corretta.</DialogDescription>
          </DialogHeader>
          {qDialog && (
            <div>
              <label className={labelCls}>Testo della domanda</label>
              <textarea rows={2} className={inputCls} value={qDialog.data.testo} onChange={(e) => setQDialog({ ...qDialog, data: { ...qDialog.data, testo: e.target.value } })} data-testid="admin-question-testo-input" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Blocco</label>
                  <select className={inputCls} value={qDialog.data.blocco} onChange={(e) => setQDialog({ ...qDialog, data: { ...qDialog.data, blocco: e.target.value } })} data-testid="admin-question-blocco-select">
                    {[1, 2, 3, 4].map((b) => <option key={b} value={b}>Blocco {b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Difficoltà</label>
                  <select className={inputCls} value={qDialog.data.difficolta} onChange={(e) => setQDialog({ ...qDialog, data: { ...qDialog.data, difficolta: e.target.value } })}>
                    <option value="facile">Facile</option>
                    <option value="media">Media</option>
                    <option value="difficile">Difficile</option>
                  </select>
                </div>
              </div>

              <label className={labelCls}>Modulo</label>
              <select className={inputCls} value={qDialog.data.moduleId} onChange={(e) => setQDialog({ ...qDialog, data: { ...qDialog.data, moduleId: e.target.value, lessonId: "" } })} data-testid="admin-question-module-select">
                <option value="">Scegli il modulo…</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.titolo}</option>)}
              </select>

              <label className={labelCls}>Lezione collegata (opzionale, per il quiz di lezione)</label>
              <select className={inputCls} value={qDialog.data.lessonId} onChange={(e) => setQDialog({ ...qDialog, data: { ...qDialog.data, lessonId: e.target.value } })}>
                <option value="">Nessuna</option>
                {lessons.filter((l) => l.moduleId === qDialog.data.moduleId).map((l) => <option key={l.id} value={l.id}>{l.titolo}</option>)}
              </select>

              <label className={labelCls}>Opzioni (seleziona la corretta)</label>
              <div className="space-y-2">
                {qDialog.data.opzioni.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="corretta"
                      checked={Number(qDialog.data.correttaIndex) === i}
                      onChange={() => setQDialog({ ...qDialog, data: { ...qDialog.data, correttaIndex: i } })}
                      className="w-4 h-4 accent-black shrink-0"
                      aria-label={`Opzione ${i + 1} corretta`}
                      data-testid="admin-question-correct-radio"
                    />
                    <input
                      className={inputCls}
                      placeholder={`Opzione ${String.fromCharCode(65 + i)}`}
                      value={opt}
                      onChange={(e) => {
                        const opts = [...qDialog.data.opzioni];
                        opts[i] = e.target.value;
                        setQDialog({ ...qDialog, data: { ...qDialog.data, opzioni: opts } });
                      }}
                      data-testid="admin-question-option-input"
                    />
                  </div>
                ))}
              </div>

              <label className={labelCls}>Spiegazione</label>
              <textarea rows={2} className={inputCls} value={qDialog.data.spiegazione} onChange={(e) => setQDialog({ ...qDialog, data: { ...qDialog.data, spiegazione: e.target.value } })} data-testid="admin-question-spiegazione-input" />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setQDialog(null)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">Annulla</button>
            <button onClick={saveQuestion} disabled={busy} data-testid="admin-save-question-button" className="rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-2.5 text-sm hover:bg-black/85 disabled:opacity-60 transition-colors duration-200">
              {busy ? "Salvataggio…" : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODULE DIALOG */}
      <Dialog open={!!mDialog} onOpenChange={(o) => !o && setMDialog(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-extrabold tracking-tight">{mDialog?.mode === "edit" ? "Modifica modulo" : "Nuovo modulo"}</DialogTitle>
            <DialogDescription>Titolo, ore e blocco d'esame.</DialogDescription>
          </DialogHeader>
          {mDialog && (
            <div>
              <label className={labelCls}>Titolo</label>
              <input className={inputCls} value={mDialog.data.titolo} onChange={(e) => setMDialog({ ...mDialog, data: { ...mDialog.data, titolo: e.target.value } })} data-testid="admin-module-titolo-input" />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Ore</label>
                  <input type="number" min="1" className={inputCls} value={mDialog.data.ore} onChange={(e) => setMDialog({ ...mDialog, data: { ...mDialog.data, ore: e.target.value } })} data-testid="admin-module-ore-input" />
                </div>
                <div>
                  <label className={labelCls}>Blocco</label>
                  <select className={inputCls} value={mDialog.data.blocco} onChange={(e) => setMDialog({ ...mDialog, data: { ...mDialog.data, blocco: e.target.value } })} data-testid="admin-module-blocco-select">
                    {[1, 2, 3, 4].map((b) => <option key={b} value={b}>Blocco {b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Ordine</label>
                  <input type="number" className={inputCls} value={mDialog.data.ordine} onChange={(e) => setMDialog({ ...mDialog, data: { ...mDialog.data, ordine: e.target.value } })} />
                </div>
              </div>
              <label className={labelCls}>Descrizione</label>
              <textarea rows={2} className={inputCls} value={mDialog.data.descrizione} onChange={(e) => setMDialog({ ...mDialog, data: { ...mDialog.data, descrizione: e.target.value } })} />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setMDialog(null)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">Annulla</button>
            <button onClick={saveModule} disabled={busy} data-testid="admin-save-module-button" className="rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-2.5 text-sm hover:bg-black/85 disabled:opacity-60 transition-colors duration-200">
              {busy ? "Salvataggio…" : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LESSON DIALOG */}
      <Dialog open={!!lDialog} onOpenChange={(o) => !o && setLDialog(null)}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-extrabold tracking-tight">{lDialog?.mode === "edit" ? "Modifica lezione" : "Nuova lezione"}</DialogTitle>
            <DialogDescription>Scheda di studio (guida con sezioni) oppure lezione classica in 4 parti.</DialogDescription>
          </DialogHeader>
          {lDialog && (
            <div>
              <label className={labelCls}>Tipo</label>
              <select
                className={inputCls}
                value={lDialog.data.type}
                onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, type: e.target.value } })}
                data-testid="admin-lesson-type-select"
              >
                <option value="scheda">Scheda di studio (guida)</option>
                <option value="classic">Lezione classica (4 parti)</option>
              </select>
              <label className={labelCls}>Modulo</label>
              <select className={inputCls} value={lDialog.data.moduleId} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, moduleId: e.target.value } })} data-testid="admin-lesson-module-select">
                <option value="">Scegli il modulo…</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.titolo}</option>)}
              </select>
              <label className={labelCls}>Titolo</label>
              <input className={inputCls} value={lDialog.data.titolo} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, titolo: e.target.value } })} data-testid="admin-lesson-titolo-input" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Ordine</label>
                  <input type="number" className={inputCls} value={lDialog.data.ordine} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, ordine: e.target.value } })} />
                </div>
                {lDialog.data.type === "scheda" ? (
                  <div>
                    <label className={labelCls}>Ore (dal programma)</label>
                    <input type="number" className={inputCls} value={lDialog.data.hours} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, hours: e.target.value } })} data-testid="admin-lesson-hours-input" />
                  </div>
                ) : (
                  <div>
                    <label className={labelCls}>URL video</label>
                    <input className={inputCls} value={lDialog.data.urlVideo} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, urlVideo: e.target.value } })} placeholder="https://…" />
                  </div>
                )}
              </div>

              {lDialog.data.type === "scheda" ? (
                <>
                  <label className={labelCls}>Introduzione</label>
                  <textarea rows={3} className={inputCls} value={lDialog.data.intro} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, intro: e.target.value } })} data-testid="admin-lesson-intro-input" />

                  <div className="flex items-center justify-between mt-4 mb-1">
                    <span className="text-xs font-bold">Sezioni</span>
                    <button
                      type="button"
                      onClick={() => setLDialog({ ...lDialog, data: { ...lDialog.data, sectionsEdit: [...lDialog.data.sectionsEdit, { heading: "", pointsText: "", key: "" }] } })}
                      data-testid="admin-lesson-add-section-button"
                      className="text-xs font-bold underline underline-offset-2"
                    >
                      + Aggiungi sezione
                    </button>
                  </div>
                  <div className="space-y-3">
                    {lDialog.data.sectionsEdit.map((sec, i) => (
                      <div key={i} className="rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-3" data-testid={`admin-lesson-section-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <input
                            className={inputCls}
                            placeholder="Titoletto sezione"
                            value={sec.heading}
                            onChange={(e) => updateSection(i, { heading: e.target.value })}
                            data-testid={`admin-section-heading-${i}`}
                          />
                          {lDialog.data.sectionsEdit.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setLDialog({ ...lDialog, data: { ...lDialog.data, sectionsEdit: lDialog.data.sectionsEdit.filter((_, j) => j !== i) } })}
                              aria-label={`Rimuovi sezione ${i + 1}`}
                              data-testid={`admin-remove-section-${i}`}
                              className="shrink-0 w-9 h-9 rounded-full border border-[color:var(--hairline)] bg-white flex items-center justify-center hover:bg-red-50 text-[color:var(--danger)] transition-colors duration-200"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <textarea
                          rows={3}
                          className={`${inputCls} mt-2`}
                          placeholder="Punti dell'elenco (uno per riga)"
                          value={sec.pointsText}
                          onChange={(e) => updateSection(i, { pointsText: e.target.value })}
                          data-testid={`admin-section-points-${i}`}
                        />
                        <input
                          className={`${inputCls} mt-2`}
                          placeholder='Riquadro "Da ricordare" (facoltativo)'
                          value={sec.key}
                          onChange={(e) => updateSection(i, { key: e.target.value })}
                          data-testid={`admin-section-key-${i}`}
                        />
                      </div>
                    ))}
                  </div>

                  <label className={labelCls}>Nota finale (rimando alle simulazioni)</label>
                  <textarea rows={2} className={inputCls} value={lDialog.data.closing} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, closing: e.target.value } })} data-testid="admin-lesson-closing-input" />
                </>
              ) : (
                <>
                  <label className={labelCls}>Testo di sintesi</label>
                  <textarea rows={4} className={inputCls} value={lDialog.data.testoSintesi} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, testoSintesi: e.target.value } })} />
                  <label className={labelCls}>Riepilogo (un punto per riga)</label>
                  <textarea rows={4} className={inputCls} value={lDialog.data.riepilogoText} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, riepilogoText: e.target.value } })} />
                  <label className={labelCls}>Mappa concettuale (JSON: centro + rami[titolo, figli])</label>
                  <textarea rows={6} className={`${inputCls} font-mono text-xs`} value={lDialog.data.mappaJson} onChange={(e) => setLDialog({ ...lDialog, data: { ...lDialog.data, mappaJson: e.target.value } })} />
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setLDialog(null)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">Annulla</button>
            <button onClick={saveLesson} disabled={busy} data-testid="admin-save-lesson-button" className="rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-2.5 text-sm hover:bg-black/85 disabled:opacity-60 transition-colors duration-200">
              {busy ? "Salvataggio…" : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-extrabold tracking-tight">Eliminare {deleting?.label}?</AlertDialogTitle>
            <AlertDialogDescription>L'operazione non si può annullare.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="admin-confirm-delete-button" className="rounded-full bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90 font-extrabold">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
